import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { usePrepareFormTagsForSetReportPayload } from 'hooks/usePrepareFormTagsForSetReportPayload';

import { ReportTemplate } from 'api/endPoints/GetReportTemplateList';
import { QueryParams as UpdateReportTemplateParams } from 'api/endPoints/UpdateReportTemplate';
import { QueryResponse as Tag } from 'api/queries/getReportsTags.query';

import { optionsToTags } from 'components/Dashboards/Reporting/ReportSettingsModal/components/UpdateReportTags/UpdateReportTags.utils';
import { UseEditReportTemplateModalProvider } from 'components/widgets/ReportsHome/ReportsTemplates/components/EditReportTemplateModal/EditReportTemplateModal.provider';

export type UseEditReportTemplateModalLogic = ReturnType<typeof useEditReportTemplateModalLogic>;

export interface FormValues {
    templateId: number | null;
    templateName: string;
    templateDescription: string;
    selectedTags: Tag[];
}

interface Props {
    api: UseEditReportTemplateModalProvider['api'];
    closeModal: () => void;
    template: ReportTemplate | null;
}

export const useEditReportTemplateModalLogic = ({ closeModal, template, api }: Props) => {
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const { getTagsPayloadForReport } = usePrepareFormTagsForSetReportPayload();

    const form = useForm<FormValues>({
        values: {
            selectedTags: template?.tags ?? [],
            templateId: template?.id ?? null,
            templateName: template?.name ?? '',
            templateDescription: template?.description ?? '',
        },
    });

    const handleTagsChange = (value: readonly ISelectOption<number>[]) => {
        const selectedTags = optionsToTags(value);
        form.setValue('selectedTags', selectedTags, { shouldDirty: true });
    };

    const convertFormDataToApiPayload = async (formData: FormValues) => {
        const selectedTagsPayload = await getTagsPayloadForReport(formData.selectedTags);
        if (formData.templateId) {
            const payload: UpdateReportTemplateParams = {
                id: formData.templateId,
                tags: selectedTagsPayload.map((tag) => tag.id),
                name: formData.templateName,
                description: formData.templateDescription,
            };
            return payload;
        }
        return null;
    };

    const handleSubmitForm = async (formData: FormValues) => {
        const payload = await convertFormDataToApiPayload(formData);
        if (payload !== null) {
            await api.editReportTemplate.instance.mutateAsync(payload, {
                onSuccess: async () => {
                    handleSnackBar(`Template "${payload.name}" has been updated successfully.`, 'success');
                    await Promise.all([
                        queryClient.invalidateQueries(['getReportTemplateList']),
                        queryClient.invalidateQueries('getAllReportTags'),
                    ]);

                    closeModal();
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    closeModal();
                },
            });
        }
    };

    return {
        logic: {
            form,
            handleSubmitForm,
            handleTagsChange,
        },
    };
};
